import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, ImagePlus, Users } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ShareBar from "../components/ShareBar";

export default function Challenge() {
  const { id } = useParams();
  const { user } = useAuth();
  const fileRef = useRef();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => { API.get(`/sakhi/challenges/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true)); };
  useEffect(() => { setC(null); setNotFound(false); load(); }, [id]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setMedia([`${process.env.REACT_APP_BACKEND_URL}${data.url}`]); toast.success("Photo added"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const submit = async () => {
    if (!user) { toast.error("Log in to participate, Sakhi 💗"); return; }
    if (!caption.trim() && !media.length) { toast.error("Add a photo or caption"); return; }
    setBusy(true);
    try { const { data } = await API.post(`/sakhi/challenges/${c.id}/submit`, { caption, media }); toast.success(data.message); setCaption(""); setMedia([]); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="challenge-notfound">
      <h1 className="font-display text-3xl font-semibold text-stone-900">Challenge not found</h1>
      <Link to="/community" className="inline-block mt-6 text-sm font-semibold text-[#6A1B29]">Back to community →</Link>
    </div>
  );
  if (!c) return <div className="min-h-[60vh] grid place-items-center text-stone-400">Loading…</div>;

  return (
    <div className="bg-[#FAF6F1] min-h-[70vh]" data-testid="challenge-page">
      <Seo title={`${c.title} | Suvikka Sakhi Challenge`} description={c.description} canonicalPath={`/challenge/${c.id}`} image={c.image} />
      <section className="relative overflow-hidden">
        {c.image && <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A1418]/90 via-[#2A1418]/70 to-[#2A1418]/40" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-white">
          <span className="inline-flex items-center gap-1.5 bg-[#6A1B29] text-white text-[11px] font-bold px-3 py-1 rounded-full"><Trophy className="w-3.5 h-3.5" /> {c.type} Challenge</span>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-4 max-w-2xl">{c.title}</h1>
          <p className="text-stone-200 mt-4 max-w-xl text-lg">{c.description}</p>
          <div className="flex items-center gap-4 mt-6 text-sm">
            <span className="inline-flex items-center gap-1.5" data-testid="challenge-participants"><Users className="w-4 h-4" /> {c.participants} participating</span>
            <ShareBar compact text={`Join the ${c.title} on Suvikka Sakhi 💗`} />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="challenge-submit-form">
          <h2 className="font-display text-2xl font-semibold text-stone-900">Submit Your Entry</h2>
          <p className="text-sm text-stone-500 mt-1">Entries are reviewed before they appear publicly.</p>
          <textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Tell us about your entry…" rows={3} className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm mt-4 outline-none focus:border-[#6A1B29]" data-testid="challenge-caption" />
          <div className="flex items-center gap-3 mt-3">
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold border border-stone-300 px-4 py-2.5 rounded-lg hover:border-[#6A1B29]" data-testid="challenge-upload"><ImagePlus className="w-4 h-4" /> Add photo</button>
            {media[0] && <img src={media[0]} alt="preview" className="w-12 h-12 rounded-lg object-cover" />}
            <button onClick={submit} disabled={busy} className="ml-auto bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-2.5 rounded-full disabled:opacity-60" data-testid="challenge-submit">{busy ? "Submitting…" : "Participate"}</button>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="font-display text-2xl font-semibold text-stone-900">Entries from the Community</h2>
        {c.submissions?.length === 0 ? (
          <p className="text-sm text-stone-500 mt-4 bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="challenge-no-entries">Be the first Sakhi to participate 💗</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6" data-testid="challenge-entries">
            {c.submissions.map((s) => (
              <div key={s.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`challenge-entry-${s.id}`}>
                {s.media?.[0] && <img src={s.media[0]} alt="" loading="lazy" className="w-full aspect-square object-cover" />}
                <div className="p-3">
                  {s.username ? <Link to={`/sakhi/${s.username}`} className="text-sm font-semibold text-stone-900 hover:text-[#6A1B29]">{s.user_name}</Link> : <p className="text-sm font-semibold text-stone-900">{s.user_name}</p>}
                  <p className="text-xs text-stone-500 mt-1 line-clamp-2">{s.caption}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
